import {
  ArrowRightOutlined,
  DownloadOutlined,
  InboxOutlined,
  StarOutlined
} from "@ant-design/icons"
import { App, Button, Flex, Typography, notification } from "antd"
import dayjs from "dayjs"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import isEqual from "react-fast-compare"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"
import {
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams
} from "react-router-dom"

import { ImportButton } from "../../../components/ImportButton.tsx"
import { SearchBar } from "../../../components/Input/SearchBar.tsx"
import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { notificationOptions } from "../../../constants/notification.config.ts"
import { ROLE_LABEL_STATUSES } from "../../../constants/role-labels.ts"
import { RoutesPaths } from "../../../constants/routes.ts"
import { useLeadImport } from "../../../hooks/lead/use-lead-import.ts"
import { useLeadStatusChange } from "../../../hooks/lead/use-lead-status-change.tsx"
import { useDebouncedCallback } from "../../../hooks/use-debounced-callback.ts"
import { nextPageUrl } from "../../../redux/shared/paged-list.ts"
import { type RootState } from "../../../store.ts"
import { downloadBlob } from "../../../utils/download-file.ts"
import { extractParamsFromNextUrl } from "../../../utils/form/extract-params-from-next-url.ts"
import { serializeData } from "../../../utils/utils"
import {
  OPERATIONAL_STATES,
  type OperationalState
} from "../../Lead/constants/operational-states.ts"
import { deleteLead } from "../../Lead/redux/lead.thunks.ts"
import type { UpdateLeadParams } from "../../Lead/redux/lead.types.ts"
import type {
  BulkLeadActionParams,
  BulkLeadResult
} from "../api/services.types.ts"
import { ArchiveLeadModal } from "../components/ArchiveLeadModal.tsx"
import { LeadImportPreviewModal } from "../components/LeadImportPreviewModal.tsx"
import { LEADS_DEFAULT_FILTERS } from "../redux/leads.constants.ts"
import { leadsSetProp } from "../redux/leads.slice.ts"
import {
  bulkUpdateLeads,
  changeLeadOperationalStates,
  exportLeads,
  fetchImportLeadsTemplates,
  fetchLeads,
  fetchLeadsFilterOptions,
  fetchLeadsStats,
  importLeads,
  previewImportLeads,
  updateLeadRow
} from "../redux/leads.thunks.ts"
import type { LeadImportPreviewRow } from "../api/services.types.ts"
import type { LeadsFilters } from "../redux/leads.types.ts"
import { type LeadListItem } from "../types/types.ts"
import { LeadsKpiRow } from "./components/LeadsKpiRow.tsx"
import { type LeadEditableData } from "./components/leads-list-table/LeadsListEditableRow.tsx"
import { LeadsListPrimarySalesModal } from "./components/leads-list-table/LeadsListPrimarySalesModal.tsx"
import LeadsListTable from "./components/leads-list-table/LeadsListTable.tsx"
import "./leads-page.less"

const { Title } = Typography

// The status filter is a multi-select over numeric option values, so the raw
// string off the URL would filter correctly but render as a bare id.
const parseStatusParam = (raw: string | null): number[] =>
  (raw ?? "")
    .split(",")
    .map((value) => Number(value.trim()))
    .filter((value) => Number.isInteger(value) && value > 0)

const parseOperationalStatesParam = (
  raw: string | null
): OperationalState[] | null => {
  const validStates = new Set(Object.values(OPERATIONAL_STATES))
  const states = (raw ?? "")
    .split(",")
    .map((value) => value.trim())
    .filter((value): value is OperationalState =>
      validStates.has(value as OperationalState)
    )

  return states.length ? states : null
}

const parseDateParam = (raw: string | null): string | null =>
  raw && /^\d{4}-\d{2}-\d{2}$/.test(raw) && dayjs(raw).isValid() ? raw : null

const parseStringListParam = (raw: string | null): string[] | null => {
  const values = (raw ?? "")
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean)

  return values.length ? values : null
}

const parseStatusListParam = (raw: string | null): number[] | null => {
  const statuses = parseStatusParam(raw)
  return statuses.length ? statuses : null
}

const parseBooleanParam = (raw: string | null): boolean | null => {
  if (raw === "true") return true
  if (raw === "false") return false
  return null
}

const LEADS_VIEWS = new Set(["active", "inactive", "all"])

// A drill whose metric is not a view — Closed-Lost spans both — has to be able
// to widen it. Anything unrecognised falls back to the page's own view rather
// than handing the table a value the server would ignore.
const parseViewParam = (raw: string | null): LeadsFilters["view"] =>
  raw && LEADS_VIEWS.has(raw)
    ? (raw as LeadsFilters["view"])
    : LEADS_DEFAULT_FILTERS.view

const parseKpiDrillFilters = (searchParams: URLSearchParams): LeadsFilters => ({
  ...LEADS_DEFAULT_FILTERS,
  view: parseViewParam(searchParams.get("view")),
  is_closed_lost: parseBooleanParam(searchParams.get("is_closed_lost")),
  archived_at_after: parseDateParam(searchParams.get("archived_at_after")),
  archived_at_before: parseDateParam(searchParams.get("archived_at_before")),
  q: searchParams.get("q") || null,
  status: parseStatusListParam(searchParams.get("status")),
  sales_manager: parseStringListParam(searchParams.get("sales_manager")),
  created_after: parseDateParam(searchParams.get("created_after")),
  created_before: parseDateParam(searchParams.get("created_before")),
  operational_states: parseOperationalStatesParam(
    searchParams.get("operational_states")
  ),
  follow_up_date_after: parseDateParam(
    searchParams.get("follow_up_date_after")
  ),
  follow_up_date_before: parseDateParam(
    searchParams.get("follow_up_date_before")
  ),
  follow_up_owner: parseStringListParam(searchParams.get("follow_up_owner")),
  source: parseStringListParam(searchParams.get("source")),
  state: searchParams.get("state") || null,
  project_type: searchParams.get("project_type") || null,
  preferred_contact_method:
    searchParams.get("preferred_contact_method") || null,
  show_converted: parseBooleanParam(searchParams.get("show_converted"))
})

const LeadsPage = ({
  user,
  leadPermissions,
  fetching,
  result,
  saveFetching,
  filters,
  filterOptions,
  updateLead,
  importLeads,
  previewImportLeads,
  exportLeads,
  bulkUpdateLeads,
  updateOperationalStates,
  getImportLeadsTemplates,
  editableLead,
  stats,
  statsFetching,
  getLeads,
  getLeadsStats,
  getLeadsFilterOptions,
  deleteLead,
  setProp
}: ConnectedProps<typeof connector>) => {
  const { confirmStatusChange } = useLeadStatusChange({
    refetchCallback: handleGetLeads
  })

  const { t } = useTranslation()
  const navigate = useNavigate()
  const { modal } = App.useApp()
  const [searchParams] = useSearchParams()
  const location = useLocation()

  const [ordering, setOrdering] = useState<string | null>(null)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [exporting, setExporting] = useState(false)
  const [leadToArchive, setLeadToArchive] = useState<LeadListItem | null>(null)
  const leadImport = useLeadImport({
    preview: previewImportLeads,
    runImport: importLeads,
    onImported: handleGetLeads
  })

  const { count, results } = result

  const hideSalesFilter = useMemo(() => {
    const EXCEPTIONS_LIST: string[] = [
      ROLE_LABEL_STATUSES.OWNER,
      ROLE_LABEL_STATUSES.ADMIN
    ]

    const isException = user?.permissions?.labels?.some((l) =>
      EXCEPTIONS_LIST?.includes(l?.code)
    )

    if (isException) return false

    // Shown to anyone who can see other people's leads — the only case where
    // filtering by owner narrows anything.
    return !leadPermissions?.features?.lead_view_all
  }, [leadPermissions?.features?.lead_view_all, user?.permissions?.labels])

  function handleGetLeads() {
    const serializeFilters = serializeData(filters) as LeadsFilters
    const data = { ordering, ...serializeFilters }
    // Page one, always: `page` is persisted with the filters, and a scrolled
    // list has no way back to the rows a restored page would skip.
    getLeads({ ...data, page: 1 })
    // The tiles are a fixed overview of the pipeline, so they take no filters.
    // They are refreshed here rather than only on mount because this is also
    // the path every mutation refetches through — a status change has to move
    // the numbers above the table, a filter change must not.
    getLeadsStats()
  }

  const debouncedGetLeads = useDebouncedCallback(handleGetLeads, 500)

  const handleChangeFilter = useCallback((newData: Partial<LeadsFilters>) => {
    // The selection is keyed on rows the new filter set may not return, and
    // `preserveSelectedRowKeys` would otherwise keep them selected invisibly.
    setSelectedIds([])
    setProp({ key: "filters", value: { ...newData, page: 1 } })
  }, [])

  const handleLoadMoreLeads = useCallback(() => {
    const url = nextPageUrl(result)
    if (!url) return
    // The server's own url carries the page and every filter it was built
    // with, so nothing here has to reconstruct them.
    getLeads({
      ...extractParamsFromNextUrl<LeadsFilters>(url),
      isMore: true
    })
  }, [result?.next, result?.retryNext, getLeads])

  const handleChangeSearch = useCallback((value?: string | null) => {
    handleChangeFilter({ q: value })
  }, [])

  const handleStatusChange = (lead: LeadListItem, payload: unknown) => {
    const targetStatus = (payload as { targetStatus?: number } | null)
      ?.targetStatus
    if (targetStatus != null) confirmStatusChange(lead, targetStatus)
  }

  function handleSelectRow(data: LeadListItem, type: string, payload: unknown) {
    switch (type) {
      case "change_status":
        return handleStatusChange(data, payload)
      case "change_operational_states":
        return updateOperationalStates({
          id: data.id,
          operational_states: payload as OperationalState[]
        })
      case "view_details":
        return navigate(RoutesPaths.lead.view(data?.id))
      case "archive":
        return setLeadToArchive(data)
      case "delete":
        modal.confirm({
          centered: true,
          title: t("Confirm deleting"),
          content: t(
            "Are you sure you want to delete this lead? This action can NOT be undone."
          ),

          cancelText: t("Cancel"),
          okText: t("Delete"),
          okButtonProps: {
            danger: true,
            ghost: true
          },
          onOk() {
            deleteLead(data?.id)
          },
          onCancel() {
            console.log("Cancel")
          }
        })
    }
  }

  const handleSetEditableLead = (editableLead: null | LeadEditableData) => {
    setProp({ key: "editableLead", value: editableLead })
  }


  const handleGetImportLeadsTemplates = async () => {
    const blob = await getImportLeadsTemplates()?.unwrap()
    downloadBlob(blob, `import-leads-template.xlsx`)
  }

  const handleExportLeads = async (ids?: string[]) => {
    try {
      setExporting(true)
      // The same filters the table is showing, minus paging — the export
      // covers the whole filtered set, not the page on screen.
      const { page: _page, page_size: _pageSize, ...exportFilters } = filters
      const params = {
        ...(serializeData(exportFilters) as LeadsFilters),
        ...(ids?.length ? { ids: ids.join(",") } : {})
      }

      const { blob, rowCount, totalCount, truncated } =
        await exportLeads(params).unwrap()

      downloadBlob(blob, `leads-${dayjs().format("YYYY-MM-DD")}.xlsx`)

      if (truncated) {
        notification.warning({
          message: "Export is incomplete",
          description: `This file holds the first ${rowCount ?? "5,000"} of ${totalCount ?? "the matching"} leads. Narrow the filters and export again for the rest.`,
          duration: 0
        })
      }
    } catch (e) {
      console.error(e)
    } finally {
      setExporting(false)
    }
  }

  const handleBulkAction = async (
    params: BulkLeadActionParams
  ): Promise<BulkLeadResult> => {
    const bulkResult = await bulkUpdateLeads(params).unwrap()
    setSelectedIds(bulkResult.failed.map((failure) => failure.id))
    handleGetLeads()
    return bulkResult
  }

  const handleUpdateLead = ({
    sales_manager,
    source,
    ...data
  }: UpdateLeadParams) => {
    const isSameSales = isEqual(sales_manager, editableLead?.sales_manager)
    const salesCount = sales_manager?.length

    const params: UpdateLeadParams = {
      ...data,
      source,
      ...(source !== editableLead?.source
        ? { source_comment: null, architect_name: null }
        : {})
    }

    if (!salesCount || isSameSales) {
      return updateLead({ ...params, sales_manager })
    }

    if (salesCount === 1) {
      return updateLead({
        ...params,
        sales_manager: [{ ...sales_manager?.[0], is_main: true }]
      })
    }

    const hasMainSales = sales_manager?.some((sale) => sale.is_main)

    if (hasMainSales) {
      return updateLead({ ...params, sales_manager })
    }

    let modalInfo: ReturnType<typeof modal.info> | null = null
    modalInfo = modal.info({
      icon: null,
      centered: true,
      title: t("leadsTable.actions.choosePrimarySales"),
      content: (
        <LeadsListPrimarySalesModal
          filterOptions={filterOptions}
          selectedSales={sales_manager}
          onClose={() => modalInfo?.destroy()}
          onConfirm={(id) => {
            updateLead({
              ...params,
              sales_manager: sales_manager?.map((s) =>
                s?.id === id ? { ...s, is_main: true } : s
              )
            })
            modalInfo?.destroy()
          }}
        />
      ),
      footer: null
    })
  }

  useEffect(() => {
    generateDocumentTitle("Active Leads")
    getLeadsFilterOptions()
  }, [])

  // Keyed on the history entry, not on mount: the KPI tiles drill to `/leads`
  // itself, so the router swaps the query string without ever remounting this
  // page — read once and a tile changes nothing but the address bar. The key
  // also distinguishes re-clicking the same tile, which the search string alone
  // cannot, while staying untouched by filter edits that never reach the URL.
  useEffect(() => {
    // Dashboard KPI tiles set `kpi`, even Active Leads which has no narrowing
    // filter. Replace rather than merge: the slice is persisted, so any filter
    // the user left behind would silently narrow the drill below its figure.
    if (searchParams.has("kpi")) {
      setProp({
        key: "filters",
        replace: true,
        value: parseKpiDrillFilters(searchParams)
      })
      return
    }

    // Preserve the established status-filter deep links.
    const status = parseStatusParam(searchParams.get("status"))
    if (status.length) {
      setProp({
        key: "filters",
        replace: true,
        value: { ...LEADS_DEFAULT_FILTERS, status }
      })
    }
  }, [location.key, location.search])

  useEffect(() => {
    debouncedGetLeads()
  }, [ordering, filters])

  return (
    <div className={"grid leads-page"}>
      {/* `min-w-0`, not `overflow-auto`. The only thing the overflow bought
          here was a grid item's min-width:0 — the table owns both its own
          scroll axes (`scroll: { x, y }`) so there was never page overflow to
          catch. What it cost was the floating "+": `overflow` makes this a
          scroll container, so the sticky action resolved against a scrollport
          that has no height cap and therefore never scrolls, leaving the
          button parked in flow at the end of the table instead of pinned
          above the fold. */}
      <Flex vertical gap={"middle"} className={"min-w-0"}>
        <Flex wrap gap={"small"} justify={"space-between"} align={"center"}>
          {/* The design pairs the title with a favourite toggle and drops the
              strapline; the KPI row directly under it carries the orientation
              the sentence used to. */}
          <Flex align={"center"} gap={10}>
            <Title className={"text-start !mb-0"} level={2}>
              {t("leadsTable.title")}
            </Title>
            {/* Saved views are a later scope, so the star is presentational
                until there is something to save it to — rendering it as a live
                control would promise a feature that does not exist. */}
            <StarOutlined
              aria-hidden
              className={"text-slate-300"}
              style={{ fontSize: 18 }}
            />
          </Flex>
          {/* The design puts search in the page header rather than in the
              filter row, so it stays reachable as the filters wrap. */}
          <Flex gap={"small"} wrap align={"center"}>
            <SearchBar
              placeholder={t("leadsTable.actions.search")}
              defaultValue={filters?.q || ""}
              onSearch={handleChangeSearch}
            />
            {leadPermissions?.leads?.lead_create && (
              <ImportButton
                onImport={leadImport.start}
                onDownloadTemplate={handleGetImportLeadsTemplates}
              />
            )}
            <Button
              onClick={() => handleExportLeads()}
              loading={exporting}
              icon={<DownloadOutlined />}
            >
              {t("leadsTable.actions.export")}
            </Button>
            {leadPermissions?.features?.lead_view_archive && (
              <Button
                type="primary"
                onClick={() => navigate(RoutesPaths.archive.leads)}
                icon={<InboxOutlined />}
              >
                {t("leadsTable.actions.inactive")}
                <ArrowRightOutlined />
              </Button>
            )}
          </Flex>
        </Flex>
        <LeadsKpiRow
          tiles={stats?.tiles}
          loading={statsFetching}
          filters={filters}
        />
        <LeadsListTable
          onSetEditableLead={handleSetEditableLead}
          editableLead={editableLead}
          ordering={ordering}
          leads={results}
          count={count}
          loading={fetching || saveFetching}
          filterOptions={filterOptions}
          filters={filters}
          user={user}
          selectedIds={selectedIds}
          //
          hideSalesFilter={hideSalesFilter}
          createAllowed={leadPermissions?.leads?.lead_create}
          editAllowed={leadPermissions?.leads?.lead_edit}
          deleteAllowed={leadPermissions?.leads?.lead_delete}
          exporting={exporting}
          assignedSalesAllowed={
            leadPermissions?.features?.lead_assign_sales_manager
          }
          showConvertedAllowed={leadPermissions?.features?.lead_view_archive}
          //
          onFiltersChange={handleChangeFilter}
          next={result?.next}
          retryNext={result?.retryNext}
          onLoadMore={handleLoadMoreLeads}
          onOrderingChange={setOrdering}
          onSelectRow={handleSelectRow}
          onEditRow={handleUpdateLead}
          onSelectedIdsChange={setSelectedIds}
          onBulkAction={handleBulkAction}
          onExportSelected={handleExportLeads}
          onAddLead={() => navigate(RoutesPaths.lead.add)}
          smartFilterCounts={stats?.smart_filters}
        />
        {leadImport.pending && (
          <LeadImportPreviewModal
            open
            rows={leadImport.pending.rows}
            loading={leadImport.importing}
            onConfirm={leadImport.confirm}
            onCancel={leadImport.cancel}
          />
        )}
        {leadToArchive && (
          <ArchiveLeadModal
            open
            leadId={leadToArchive.id}
            leadName={leadToArchive.lead_name}
            onCancel={() => setLeadToArchive(null)}
            onArchived={() => setLeadToArchive(null)}
          />
        )}
      </Flex>

      <Outlet />
    </div>
  )
}

const mapStateToProps = (state: RootState) => ({
  user: state?.auth?.userInfo,
  fetching: state.leads.fetching,
  result: state.leads.result,
  editableLead: state.leads.editableLead,
  stats: state.leads.stats,
  statsFetching: state.leads.statsFetching,
  filters: state.leads.filters,
  filterOptions: state.leads.filterOptions,
  saveFetching: state.leads.saveFetching,
  leadPermissions: state?.auth?.permissions?.codes?.leads
})

const mapDispatchToProps = {
  getLeadsFilterOptions: fetchLeadsFilterOptions,
  getLeads: fetchLeads,
  getLeadsStats: fetchLeadsStats,
  deleteLead: deleteLead,
  updateLead: updateLeadRow,
  setProp: leadsSetProp,
  importLeads,
  previewImportLeads,
  getImportLeadsTemplates: fetchImportLeadsTemplates,
  exportLeads,
  bulkUpdateLeads,
  updateOperationalStates: changeLeadOperationalStates
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(LeadsPage)
